import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-sm)] text-body font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      // Tokens du design system, pas la palette grise par défaut de
      // Tailwind : le bouton primaire de l'app était `bg-slate-900`, donc
      // noir-ardoise, alors que la marque est `--color-brand`.
      variant: {
        primary: 'bg-brand text-brand-ink hover:bg-brand/90',
        secondary: 'bg-surface-alt text-ink hover:bg-hair',
        outline: 'border border-hair bg-surface text-ink hover:bg-surface-alt',
        // Pas de couleur de texte ici : plusieurs appelants passent la
        // leur (`text-danger` sur « Désactiver », `text-ink-mute` sur les
        // icônes d'archivage). Une classe de couleur dans la variante la
        // neutraliserait — à spécificité égale, c'est l'ordre dans le CSS
        // bâti qui tranche, et `text-ink-soft` y sort après `text-danger`.
        ghost: 'hover:bg-surface-alt',
        danger: 'bg-danger text-white hover:bg-danger/90',
      },
      // Plancher tactile de 44 px (Apple HIG 44 pt, Material 48 dp) sur les
      // commandes que le doigt vise vraiment : `md` (le bouton par défaut)
      // et `icon`. 40 → 44 px, un écart de 10 % invisible à l'œil.
      //
      // `sm` reste à 32 px, DÉLIBÉRÉMENT : il ne sert que dans les écrans
      // admin denses (Configuration, Utilisateurs), au clavier et à la
      // souris, et le porter à 44 y coûtait la densité sans rien gagner sur
      // le terrain. 44 px relève de WCAG 2.5.5 niveau AAA ; le critère
      // visé, 2.5.8 niveau AA, demande 24 px — 32 est donc conforme.
      // Décision Valentin du 07/09/2026, sur comparaison à taille réelle.
      size: {
        sm: 'h-8 px-3',
        md: 'h-11 px-4',
        lg: 'h-12 px-6 text-strong',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  ),
);
Button.displayName = 'Button';
